/* eslint react-hooks/exhaustive-deps: "warn" */
import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import type { FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';

import styles from './DataLicensingRedistributionHero.module.scss';

type Translate = (key: string) => string;

const getStats = (t: Translate): FeatureGridItem[] => [
  { value: '400', suffix: '+', title: t('Government registries sourced directly') },
  { value: '200', suffix: '+', title: t('Countries under one worldwide licence') },
  { value: '600', suffix: 'M+', title: t('Company profiles with field-level provenance') },
  { value: t('Daily'), title: t('Synced change feeds where registries publish them') },
];

const REGISTRIES = [
  { flag: '🇬🇧', name: 'Companies House', country: 'United Kingdom' },
  { flag: '🇩🇪', name: 'Handelsregister', country: 'Germany' },
  { flag: '🇫🇷', name: 'Registre du Commerce et des Sociétés', country: 'France' },
  { flag: '🇳🇱', name: 'KVK Handelsregister', country: 'Netherlands' },
  { flag: '🇺🇸', name: 'SEC EDGAR', country: 'United States' },
  { flag: '🇮🇹', name: 'Registro Imprese', country: 'Italy' },
  { flag: '🇪🇸', name: 'Registro Mercantil', country: 'Spain' },
  { flag: '🇸🇪', name: 'Bolagsverket', country: 'Sweden' },
  { flag: '🇸🇬', name: 'ACRA', country: 'Singapore' },
  { flag: '🇨🇦', name: 'Corporations Canada', country: 'Canada' },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy', country: 'Poland' },
  { flag: '🇦🇺', name: 'ASIC', country: 'Australia' },
  { flag: '🇨🇭', name: 'Zefix', country: 'Switzerland' },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene', country: 'Norway' },
  { flag: '🇮🇳', name: 'Ministry of Corporate Affairs', country: 'India' },
  { flag: '🇧🇪', name: 'Kruispuntbank van Ondernemingen', country: 'Belgium' },
  { flag: '🇯🇵', name: 'National Tax Agency', country: 'Japan' },
  { flag: '🇩🇰', name: 'Erhvervsstyrelsen CVR', country: 'Denmark' },
  { flag: '🇮🇪', name: 'Companies Registration Office', country: 'Ireland' },
  { flag: '🇧🇷', name: 'Receita Federal CNPJ', country: 'Brazil' },
  { flag: '🇦🇹', name: 'Firmenbuch', country: 'Austria' },
  { flag: '🇭🇰', name: 'Companies Registry', country: 'Hong Kong' },
  { flag: '🇫🇮', name: 'Patentti- ja rekisterihallitus', country: 'Finland' },
  { flag: '🇿🇦', name: 'CIPC', country: 'South Africa' },
  { flag: '🇨🇿', name: 'Obchodní rejstřík', country: 'Czechia' },
  { flag: '🇵🇹', name: 'Registo Comercial', country: 'Portugal' },
  { flag: '🇳🇿', name: 'Companies Office', country: 'New Zealand' },
  { flag: '🇱🇺', name: 'RCS Luxembourg', country: 'Luxembourg' },
  { flag: '🇬🇷', name: 'GEMI', country: 'Greece' },
  { flag: '🇲🇽', name: 'Registro Público de Comercio', country: 'Mexico' },
];

const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path
      d="M3.3 1.7 5.2 4l-1.3 1.6a9.3 9.3 0 0 0 5.5 5.5L10.9 9.8l2.4 1.9v2.1a1 1 0 0 1-1.1 1C6 14.4 1.5 9.9 1.1 3.7v-1a1 1 0 0 1 1-1z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path
      d="M3 7.5h9M8.5 3.5l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DataLicensingRedistributionHero = () => {
  const { t } = useInt();

  return (
    <div className={styles.root}>
      <Section background="#0E1526" className={styles.heroSection}>
        <div className={styles.glow} aria-hidden="true" />

        <TitleBlock
          size="page"
          align="left"
          theme="dark"
          title={t('License registry data to power your own product')}
          description={t(
            'Primary, government-sourced company data from 400+ national registries, delivered with redistribution rights, worldwide territory, and a perpetual licence on every record we send you.',
          )}
        />

        <div className={styles.actions}>
          <Button type="white" size="xxl" href="#clause" suffix={<ArrowIcon />}>
            {t('Talk to licensing')}
          </Button>

          <Button type="white-outline" size="xxl" href="/free-trial">
            {t('Request a sample file')}
          </Button>

          <a className={styles.tel} href="tel:+442045511901">
            <span className={styles.telIcon}>
              <PhoneIcon />
            </span>
            {t('or call the licensing desk')} <b>+44 20 4551 1901</b>
          </a>
        </div>

        <StatsBlock features={getStats(t)} theme="dark" hasPadding={false} transparent className={styles.statsBlock} />
      </Section>

      <div className={styles.marquee} aria-label={t('Government registries we source from directly')}>
        <div className={styles.marqueeGlow} aria-hidden="true" />

        <span className={styles.marqueeLabel}>
          <span className={styles.marqueeLive} aria-hidden="true" />
          {t('Connected directly to 400+ national registries')}
        </span>

        <div className={styles.marqueeMask}>
          <div className={styles.marqueeTrack}>
            {[...REGISTRIES, ...REGISTRIES].map((registry, i) => (
              <span key={`${registry.name}-${i}`} className={styles.marqueeItem}>
                <span className={styles.marqueeFlag} aria-hidden="true">
                  {registry.flag}
                </span>
                <b>{registry.name}</b>
                <i>{registry.country}</i>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
